import { useEffect, useState } from "react";

/**
 * Devuelve `true` cuando ya es buen momento para montar algo no crítico:
 * tras la primera interacción real del visitante (scroll, puntero, teclado,
 * touch) o, si no interactúa, cuando el navegador queda inactivo.
 *
 * Se usa para no descargar scripts de terceros (p. ej. Google Identity
 * Services, ~96 KiB) durante el arranque de la portada.
 */
export function useDeferredMount(idleTimeoutMs = 4000): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let done = false;
    const events = ["pointerdown", "keydown", "touchstart", "scroll", "wheel"] as const;

    const fire = () => {
      if (done) return;
      done = true;
      cleanup();
      setReady(true);
    };

    const cleanup = () => {
      for (const ev of events) window.removeEventListener(ev, fire);
      window.clearTimeout(timer);
      if (idleHandle !== undefined) {
        const cancelIdle = (window as unknown as { cancelIdleCallback?: (h: number) => void })
          .cancelIdleCallback;
        cancelIdle?.(idleHandle);
      }
    };

    for (const ev of events) {
      window.addEventListener(ev, fire, { passive: true, once: true });
    }

    // Red de seguridad opcional: si el visitante no toca nada, montamos en el
    // primer hueco de inactividad. Con idleTimeoutMs = Infinity el montaje
    // depende SOLO de una interacción real (así los scripts de terceros no se
    // descargan nunca en una auditoría automática sin interacción).
    const interactionOnly = !Number.isFinite(idleTimeoutMs);
    const timer = interactionOnly ? 0 : window.setTimeout(fire, idleTimeoutMs);

    let idleHandle: number | undefined;
    const requestIdle = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    if (!interactionOnly && requestIdle) {
      idleHandle = requestIdle(fire, { timeout: idleTimeoutMs });
    }

    return cleanup;
  }, [idleTimeoutMs, ready]);

  return ready;
}
